import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaInfoCircle, FaEdit, FaSave } from 'react-icons/fa';
import { logout } from '../../../../../utils/api/auth';
import axiosInstance from '../../../../../utils/api/axiosInstance';
import { updateEventStatus, updateEventDetails } from '../../../../../utils/api/adminApi';
import { DEFAULT_IMAGE_DATA_URI } from '../../../../../components/imageUtils';
import GlobalHeader from '../../../../../components/GlobalHeader.jsx';
import ReadOnlyEventDetailsCard from '../../../../customer/EventDetailsForPurchase/components/ReadOnlyEventDetailsCard.jsx';
import EditableEventDetailsCard from '../components/EditableEventDetailsCard.jsx';
import ImageCard from '../../../../customer/EventDetailsForPurchase/components/ImageCard.jsx';
import '../css/AdminViewEventRequest.css';

const locationToString = (loc) => {
  if (!loc) return '';
  if (typeof loc === 'object') return loc.formatted_address || loc.street_address || '';
  if (typeof loc === 'string') {
    try {
      const parsed = JSON.parse(loc);
      return parsed.formatted_address || parsed.street_address || loc;
    } catch {
      return loc;
    }
  }
  return String(loc);
};

const AdminViewEventRequest = () => {
  const nav = useNavigate();
  const location = useLocation();
  const { eventid, fromEventsHistory } = location.state || {};
  const isFromEventsHistory = fromEventsHistory === true;

  const [event, setEvent] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [editPackages, setEditPackages] = useState([]);

  const handleBackNavigation = () => {
    if (isFromEventsHistory) {
      nav('/events-history');
    } else {
      nav('/event-approval');
    }
  };

  const handleLogout = () => {
    logout(nav);
  };

  useEffect(() => {
    if (!eventid) {
      setError('No event ID provided');
      setLoading(false);
      return;
    }

    const fetchEvent = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(`/admin/events/${eventid}`);
        const data = response.data;

        const parseTabs = (tabsData) => {
          if (!tabsData) return [];
          if (Array.isArray(tabsData)) {
            return tabsData.map((tab) => {
              if (typeof tab === 'string') {
                try {
                  const parsedTab = JSON.parse(tab);
                  return {
                    name: parsedTab.name || '',
                    content: parsedTab.content || '',
                  };
                } catch {
                  return { name: 'Unnamed Tab', content: tab };
                }
              }
              return {
                name: tab.name || '',
                content: tab.content || '',
              };
            });
          }
          if (typeof tabsData === 'string') {
            try {
              const parsedTabs = JSON.parse(tabsData);
              return Array.isArray(parsedTabs)
                ? parsedTabs.map((tab) => ({
                    name: tab.name || '',
                    content: tab.content || '',
                  }))
                : [{ name: 'Unnamed Tab', content: tabsData }];
            } catch {
              return [{ name: 'Unnamed Tab', content: tabsData }];
            }
          }
          return [];
        };

        const normalisedEvent = {
          ...data,
          event_name: data.Name || data.name || data.event_name || 'Untitled Event',
          file_url: data.coverimage || data.MainImage || data.file_url || DEFAULT_IMAGE_DATA_URI,
          location: data.Location || data.location || data.venue || 'Not specified',
          event_type: data.Type || data.type || data.event_type || 'Standard',
          capacity: data.TotalTickets || data.capacity || '',
          start_date: data.StartDate || data.startdate || data.start_date || '',
          end_date: data.EndDate || data.enddate || data.end_date || '',
          deadline: data.DeadlineDate || data.deadline || '',
          time: data.StartTime || data.time || data.start_time || '',
          description: data.About || data.description || '',
          terms_and_conditions: data["Terms&Conditions"] || data.terms_and_conditions || '',
          tabs: parseTabs(data.tabs),
          packages: data.packages || [],
        };

        setEvent(normalisedEvent);
      } catch (err) {
        setError(err.message || 'Failed to load event');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventid]);

  const handleStatusUpdate = async (status) => {
    if (!eventid) {
      setError('No event ID provided');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await updateEventStatus(eventid, status, '');
      nav('/event-approval');
    } catch (err) {
      setError(`Error: ${err.message || 'Failed to update status'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toDateInput = (value) => {
    if (!value) return '';
    // If the value is already a plain "YYYY-MM-DD" (or starts with one), use it directly
    // to avoid UTC/local timezone conversion shifting the calendar day.
    const isoMatch = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoMatch) return isoMatch[1];
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const toTimeInput = (value) => {
    if (!value) return '';
    // Accept "HH:MM" or "HH:MM:SS"
    const match = String(value).match(/^(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}` : '';
  };

  const handleEdit = () => {
    if (!event) return;
    setFormData({
      event_name: event.event_name || '',
      location: event.location || '',
      event_type: event.event_type || '',
      capacity: event.capacity ?? '',
      start_date: toDateInput(event.start_date),
      end_date: toDateInput(event.end_date),
      time: toTimeInput(event.time),
      endtime: toTimeInput(event.endtime || event.EndTime || event.end_time),
      description: event.description || '',
      terms_and_conditions: event.terms_and_conditions || '',
    });
    setEditPackages(
      (event.packages || []).map((pkg) => ({
        package_id: pkg.package_id ?? pkg.PackageID,
        name: pkg.name ?? pkg.packageType ?? '',
        description: pkg.description ?? pkg.details ?? '',
        price: pkg.price ?? pkg.pricing ?? '',
        max_tickets: pkg.max_tickets ?? pkg.numberOfTickets ?? '',
        duration: pkg.duration ?? '',
        valid_from_date: pkg.valid_from_date ?? pkg.validFromDate ?? '',
        valid_to_date: pkg.valid_to_date ?? pkg.validToDate ?? '',
        valid_from_time: pkg.valid_from_time ?? pkg.validFromTime ?? '',
        valid_to_time: pkg.valid_to_time ?? pkg.validToTime ?? '',
      }))
    );
    setActiveTab(0);
    setIsEditing(true);
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePackageChange = (index, field, value) => {
    setEditPackages((prev) =>
      prev.map((pkg, i) => (i === index ? { ...pkg, [field]: value } : pkg))
    );
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setFormData({});
    setEditPackages([]);
  };

  const handleSave = async () => {
    if (!eventid) return;
    setIsSaving(true);
    setError(null);

    const payload = {
      name: formData.event_name,
      location: locationToString(formData.location),
      type: formData.event_type,
      capacity: formData.capacity,
      start_date: formData.start_date,
      end_date: formData.end_date,
      time: formData.time,
      endtime: formData.endtime,
      description: formData.description,
      terms_and_conditions: formData.terms_and_conditions,
      packages: editPackages.map((pkg) => ({
        package_id: pkg.package_id,
        name: pkg.name,
        description: pkg.description,
        price: pkg.price,
        max_tickets: pkg.max_tickets,
        duration: pkg.duration,
      })),
    };

    try {
      await updateEventDetails(eventid, payload);
      // Reflect the saved values back into the displayed event
      setEvent((prev) => ({
        ...prev,
        event_name: formData.event_name,
        location: locationToString(formData.location),
        event_type: formData.event_type,
        capacity: formData.capacity,
        start_date: formData.start_date,
        end_date: formData.end_date,
        time: formData.time,
        endtime: formData.endtime,
        description: formData.description,
        terms_and_conditions: formData.terms_and_conditions,
        packages: (prev.packages || []).map((pkg) => {
          const edited = editPackages.find(
            (p) => p.package_id === (pkg.package_id ?? pkg.PackageID)
          );
          return edited
            ? {
                ...pkg,
                name: edited.name,
                packageType: edited.name,
                description: edited.description,
                details: edited.description,
                price: edited.price,
                pricing: edited.price,
                max_tickets: edited.max_tickets,
                numberOfTickets: edited.max_tickets,
                duration: edited.duration,
              }
            : pkg;
        }),
      }));
      setIsEditing(false);
    } catch (err) {
      setError(`Error: ${err.response?.data?.error || err.message || 'Failed to save changes'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const buildReadOnlyEvent = () => {
    if (!event) return null;

    const dateLabel = (() => {
      if (event.start_date && event.end_date && event.end_date !== event.start_date) {
        const start = new Date(event.start_date).toLocaleDateString();
        const end = new Date(event.end_date).toLocaleDateString();
        return `${start} - ${end}`;
      }
      if (event.start_date) {
        return new Date(event.start_date).toLocaleDateString();
      }
      return event.date || '';
    })();

    return {
      ...event,
      date: dateLabel,
      name: event.event_name || event.name || 'Untitled Event',
      organizer: event.organizer || event.organiser || 'XPress Events',
      category: event.category || event.event_type || 'General',
    };
  };

  const readOnlyEvent = buildReadOnlyEvent();

  return (
    <div className="admin-view-event-page">
      <GlobalHeader
        title="Event Request Review"
        subtitle="Review and manage event details"
        onBack={handleBackNavigation}
        onLogout={handleLogout}
        backTo={isFromEventsHistory ? '/events-history' : '/event-approval'}
      />

      <main className="admin-view-event-main">
        {loading && (
          <div className="admin-view-event__state admin-view-event__state--loading">
            <div className="admin-view-event__spinner" />
            <p>Loading event details...</p>
          </div>
        )}

        {!loading && error && (
          <div className="admin-view-event__state admin-view-event__state--error">
            <FaInfoCircle className="admin-view-event__state-icon" />
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && !event && (
          <div className="admin-view-event__state admin-view-event__state--empty">
            <FaInfoCircle className="admin-view-event__state-icon" />
            <p>No event data available.</p>
          </div>
        )}

        {!loading && !error && event && (
          <>
            <div className="admin-view-event__edit-controls">
              <button
                type="button"
                className="admin-view-event__edit-btn"
                onClick={isEditing ? handleCancelEdit : handleEdit}
              >
                <FaEdit /> {isEditing ? 'Cancel' : 'Edit'}
              </button>
              {isEditing && (
                <button
                  type="button"
                  className="admin-view-event__save-btn"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  <FaSave /> {isSaving ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>

            <section className="admin-view-event__image">
              <ImageCard event={readOnlyEvent} />
            </section>

            <section className="admin-view-event__details-card">
              {isEditing ? (
                <EditableEventDetailsCard
                  formData={formData}
                  onFieldChange={handleFieldChange}
                  packages={editPackages}
                  onPackageChange={handlePackageChange}
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                />
              ) : (
                <ReadOnlyEventDetailsCard
                  event={readOnlyEvent}
                  packages={event.packages || []}
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  onSelectPackage={undefined}
                  showSelectButton={false}
                />
              )}
            </section>

            {!isFromEventsHistory && !isEditing && (
              <div className="admin-view-event__actions-row">
                <button
                  type="button"
                  className="admin-view-event__button admin-view-event__button--reject"
                  onClick={() => handleStatusUpdate('Rejected')}
                  disabled={isSubmitting}
                >
                  Reject Event
                </button>
                <button
                  type="button"
                  className="admin-view-event__button admin-view-event__button--approve"
                  onClick={() => handleStatusUpdate('Approved')}
                  disabled={isSubmitting}
                >
                  Approve Event
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default AdminViewEventRequest;
